import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Save, X, Calendar, User, Building2, FileText, Link as LinkIcon, Zap, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { toast } from 'sonner';
import RichTextEditor from './RichTextEditor';
import './ModuloBase.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WIZARD_STEPS = [
  { num: 1, title: 'Datos Generales', desc: 'Institución y asignación' },
  { num: 2, title: 'Campos Específicos', desc: 'Datos del módulo' },
  { num: 3, title: 'Descripción y Documentación', desc: 'Hallazgo y enlaces' }
];

const FormularioGestion = ({ 
  tipoGestion, 
  nomenclatura,
  onSuccess, 
  onCancel,
  camposEspecificos 
}) => {
  const [loading, setLoading] = useState(false);
  const [instituciones, setInstituciones] = useState([]);
  const [asesores, setAsesores] = useState([]);
  const [asesorSugerido, setAsesorSugerido] = useState(null);
  const [usarAsignacionAuto, setUsarAsignacionAuto] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const [stepErrors, setStepErrors] = useState({});
  
  const [formData, setFormData] = useState({
    tipo_gestion: tipoGestion,
    institucion: '',
    nombre_asesor_cnc: '',
    nombre_asesor_instancia: '',
    fecha_asignacion: new Date().toISOString().split('T')[0],
    fecha_vencimiento: '',
    descripcion: '',
    links_referenciales: [],
    estado: 'Registrado',
    campos_especificos: {}
  });

  const [currentLink, setCurrentLink] = useState('');

  const fetchAsesorDisponible = useCallback(async (institucion = null) => {
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (institucion) {
        params.institucion = institucion;
      }
      const response = await axios.get(`${API}/asignacion/asesor-cnc-disponible`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      if (response.data.asesor) {
        setAsesorSugerido(response.data);
        if (usarAsignacionAuto) {
          setFormData(prev => ({
            ...prev,
            nombre_asesor_cnc: response.data.asesor.nombre_completo
          }));
        }
      } else {
        setAsesorSugerido(null);
      }
    } catch (error) {
      console.error('Error al obtener asesor disponible:', error);
    }
  }, [usarAsignacionAuto]);

  useEffect(() => {
    fetchCatalogos();
    fetchAsesorDisponible();
  }, []);

  useEffect(() => {
    if (formData.institucion) {
      fetchAsesorDisponible(formData.institucion);
    }
  }, [formData.institucion, fetchAsesorDisponible]);

  const fetchCatalogos = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const instResponse = await axios.get(`${API}/catalogs/institutions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const institucionesActivas = (instResponse.data || []).filter(inst => inst.is_active);
      setInstituciones(institucionesActivas);

      const asesoresResponse = await axios.get(`${API}/catalogos/asesores-cnc?activo=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAsesores(asesoresResponse.data || []);
    } catch (error) {
      console.error('Error al cargar catálogos:', error);
      if (!instituciones.length && !asesores.length) {
        toast.error('Error al cargar catálogos');
      }
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCampoEspecifico = (field, value) => {
    setFormData(prev => ({
      ...prev,
      campos_especificos: {
        ...prev.campos_especificos,
        [field]: value
      }
    }));
  };

  const handleAddLink = () => {
    if (currentLink.trim()) {
      setFormData(prev => ({
        ...prev,
        links_referenciales: [...prev.links_referenciales, currentLink.trim()]
      }));
      setCurrentLink('');
    }
  };

  const handleRemoveLink = (index) => {
    setFormData(prev => ({
      ...prev,
      links_referenciales: prev.links_referenciales.filter((_, i) => i !== index)
    }));
  };

  const validateStep = (step) => {
    const errors = {};
    switch (step) {
      case 1:
        if (!formData.institucion) errors.institucion = 'Institución es requerida';
        if (!formData.nombre_asesor_cnc) errors.nombre_asesor_cnc = 'Asesor es requerido';
        if (!formData.fecha_asignacion) errors.fecha_asignacion = 'Fecha de asignación es requerida';
        break;
      case 2:
        // Validation depends on module - camposEspecificos handles its own required fields
        break;
      case 3:
        break;
      default:
        break;
    }
    setStepErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 3));
    } else {
      toast.error('Complete los campos requeridos antes de continuar');
    }
  };

  const handlePrevStep = () => {
    setStepErrors({});
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/gestiones`, formData);
      toast.success(`Gestión ${nomenclatura} creada exitosamente`);
      onSuccess();
    } catch (error) {
      console.error('Error al crear gestión:', error);
      toast.error(error.response?.data?.detail || 'Error al crear la gestión');
    } finally {
      setLoading(false);
    }
  };

  const totalSteps = camposEspecificos ? 3 : 2;
  const steps = camposEspecificos 
    ? WIZARD_STEPS 
    : [WIZARD_STEPS[0], WIZARD_STEPS[2]];

  const getActualStep = () => {
    if (!camposEspecificos && currentStep === 2) return 3;
    return currentStep;
  };

  return (
    <div className="wizard-container" data-testid="wizard-formulario-gestion">
      {/* Progress Bar */}
      <div className="wizard-progress" data-testid="wizard-progress">
        {steps.map((step, idx) => {
          const stepNum = idx + 1;
          return (
            <div 
              key={step.num} 
              className={`wizard-step-indicator ${currentStep === stepNum ? 'active' : ''} ${currentStep > stepNum ? 'completed' : ''}`}
              onClick={() => { if (currentStep > stepNum) { setStepErrors({}); setCurrentStep(stepNum); } }}
              style={{ cursor: currentStep > stepNum ? 'pointer' : 'default' }}
              data-testid={`wizard-step-indicator-${stepNum}`}
            >
              <div className="step-circle">
                {currentStep > stepNum ? <Check size={14} /> : stepNum}
              </div>
              <div className="step-label">
                <span className="step-title">{step.title}</span>
                <span className="step-desc">{step.desc}</span>
              </div>
              {idx < steps.length - 1 && <div className={`step-connector ${currentStep > stepNum ? 'completed' : ''}`} />}
            </div>
          );
        })}
      </div>

      <form onSubmit={handleSubmit}>
        {/* Step 1: Datos Generales */}
        {currentStep === 1 && (
          <div className="modulo-form-section" data-testid="wizard-step-1">
            <h3 className="modulo-form-section-title">
              <Calendar size={18} />
              Paso 1: Datos Generales
            </h3>
            <div className="form-grid">
              <div className="form-field">
                <label className="required">
                  <Building2 size={16} />
                  Institución
                </label>
                <select
                  value={formData.institucion}
                  onChange={(e) => handleChange('institucion', e.target.value)}
                  className={stepErrors.institucion ? 'input-error' : ''}
                  data-testid="wizard-institucion"
                >
                  <option value="">Seleccione una institución</option>
                  {instituciones.map((inst) => (
                    <option key={inst.id} value={inst.value}>
                      {inst.value}
                    </option>
                  ))}
                </select>
                {stepErrors.institucion && <span className="field-error">{stepErrors.institucion}</span>}
              </div>

              <div className="form-field">
                <label className="required">
                  <User size={16} />
                  Asesor
                </label>
                {asesorSugerido && (
                  <div style={{ 
                    background: asesorSugerido.asesor.grupo_id ? 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)' : 'linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)',
                    border: asesorSugerido.asesor.grupo_id ? '1px solid #4CAF50' : '1px solid #FF9800',
                    borderRadius: '6px', padding: '0.5rem 0.75rem', marginBottom: '0.5rem', fontSize: '0.8rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Zap size={16} color={asesorSugerido.asesor.grupo_id ? '#4CAF50' : '#FF9800'} />
                      <span>
                        <strong>Sugerido:</strong> {asesorSugerido.asesor.nombre_completo}
                        <span style={{ color: '#666', marginLeft: '0.5rem' }}>({asesorSugerido.casos_asignados} casos)</span>
                      </span>
                    </div>
                    {asesorSugerido.asesor.grupo_nombre && (
                      <div style={{ marginTop: '0.25rem', color: '#1976D2', fontSize: '0.75rem' }}>Grupo: {asesorSugerido.asesor.grupo_nombre}</div>
                    )}
                    {usarAsignacionAuto && (
                      <div style={{ marginTop: '0.25rem', color: '#388E3C', fontSize: '0.7rem' }}>
                        Asignación automática activa
                      </div>
                    )}
                  </div>
                )}
                <select
                  value={formData.nombre_asesor_cnc}
                  onChange={(e) => {
                    handleChange('nombre_asesor_cnc', e.target.value);
                    setUsarAsignacionAuto(false);
                  }}
                  className={stepErrors.nombre_asesor_cnc ? 'input-error' : ''}
                  data-testid="wizard-asesor"
                >
                  <option value="">Seleccione un asesor</option>
                  {asesores.map((asesor) => (
                    <option key={asesor.id} value={asesor.nombre_completo}>
                      {asesor.nombre_completo}
                    </option>
                  ))}
                </select>
                {stepErrors.nombre_asesor_cnc && <span className="field-error">{stepErrors.nombre_asesor_cnc}</span>}
                <small style={{ color: 'var(--color-text-muted)', fontSize: '0.75rem' }}>
                  El sistema sugiere al asesor con menor carga según la institución seleccionada
                </small>
              </div>

              <div className="form-field">
                <label>
                  <User size={16} />
                  Instancia de Probidad
                  <span style={{ fontSize: '0.7rem', color: '#FF9800', marginLeft: '0.5rem' }}>(Se asigna en derivación)</span>
                </label>
                <input
                  type="text"
                  value={formData.nombre_asesor_instancia}
                  disabled
                  placeholder="Se asignará después del análisis"
                  style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                />
              </div>

              <div className="form-field">
                <label className="required">
                  <Calendar size={16} />
                  Fecha de asignación
                </label>
                <input
                  type="date"
                  value={formData.fecha_asignacion}
                  onChange={(e) => handleChange('fecha_asignacion', e.target.value)}
                  className={stepErrors.fecha_asignacion ? 'input-error' : ''}
                  data-testid="wizard-fecha-asignacion"
                />
                {stepErrors.fecha_asignacion && <span className="field-error">{stepErrors.fecha_asignacion}</span>}
              </div>

              <div className="form-field">
                <label>
                  <Calendar size={16} />
                  Fecha vencimiento
                </label>
                <input
                  type="date"
                  value={formData.fecha_vencimiento}
                  onChange={(e) => handleChange('fecha_vencimiento', e.target.value)}
                />
              </div>

              <div className="form-field">
                <label>Estado Inicial</label>
                <input
                  type="text"
                  value="Registrado"
                  disabled
                  style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                />
                <small style={{ color: 'var(--color-text-muted)', fontSize: '0.75rem' }}>
                  El estado inicial es "Registrado". Puede cambiarlo después.
                </small>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Campos Específicos del Módulo (solo si tiene camposEspecificos) */}
        {camposEspecificos && currentStep === 2 && (
          <div className="modulo-form-section" data-testid="wizard-step-2">
            <h3 className="modulo-form-section-title">
              <FileText size={18} />
              Paso 2: Campos Específicos - {tipoGestion}
            </h3>
            {camposEspecificos(formData, handleCampoEspecifico)}
          </div>
        )}

        {/* Step 3: Descripción y Documentación */}
        {currentStep === totalSteps && (
          <div data-testid="wizard-step-3">
            <div className="modulo-form-section">
              <h3 className="modulo-form-section-title">
                <FileText size={18} />
                Paso {totalSteps}: Descripción y Documentación
              </h3>
              <div className="form-field">
                <label className="required">
                  <FileText size={16} />
                  Descripción / Hallazgo
                </label>
                <RichTextEditor
                  value={formData.descripcion}
                  onChange={(value) => handleChange('descripcion', value)}
                  placeholder="Descripción detallada del caso o hallazgo identificado..."
                  minHeight="150px"
                />
              </div>

              <div className="form-field" style={{ marginTop: '1rem' }}>
                <label>
                  <LinkIcon size={16} />
                  Links Referenciales
                </label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="url"
                    value={currentLink}
                    onChange={(e) => setCurrentLink(e.target.value)}
                    placeholder="https://ejemplo.com"
                    data-testid="wizard-link-input"
                  />
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={handleAddLink}
                    data-testid="wizard-add-link-btn"
                  >
                    Agregar
                  </button>
                </div>
                {formData.links_referenciales.length > 0 && (
                  <div style={{ marginTop: '0.5rem' }}>
                    {formData.links_referenciales.map((link, index) => (
                      <div 
                        key={index} 
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '0.5rem',
                          padding: '0.5rem',
                          background: 'var(--color-background)',
                          borderRadius: '4px',
                          marginBottom: '0.25rem'
                        }}
                      >
                        <LinkIcon size={14} />
                        <a href={link} target="_blank" rel="noopener noreferrer" style={{ flex: 1, fontSize: '0.875rem' }}>
                          {link}
                        </a>
                        <button
                          type="button"
                          onClick={() => handleRemoveLink(index)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#F44336' }}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Wizard Navigation */}
        <div className="wizard-navigation" data-testid="wizard-navigation">
          <button 
            type="button" 
            className="btn btn-secondary" 
            onClick={onCancel}
            disabled={loading}
            data-testid="wizard-cancel-btn"
          >
            <X size={18} />
            Cancelar
          </button>

          <div className="wizard-nav-center">
            {currentStep > 1 && (
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={handlePrevStep}
                data-testid="wizard-prev-btn"
              >
                <ChevronLeft size={18} />
                Anterior
              </button>
            )}
            <span className="wizard-step-counter" data-testid="wizard-step-counter">
              Paso {currentStep} de {totalSteps}
            </span>
            {currentStep < totalSteps && (
              <button 
                type="button" 
                className="btn btn-primary" 
                onClick={handleNextStep}
                data-testid="wizard-next-btn"
              >
                Siguiente
                <ChevronRight size={18} />
              </button>
            )}
          </div>

          {currentStep === totalSteps && (
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading}
              data-testid="wizard-submit-btn"
            >
              <Save size={18} />
              {loading ? 'Guardando...' : 'Crear Gestión'}
            </button>
          )}
          {currentStep !== totalSteps && <div style={{ width: '140px' }} />}
        </div>
      </form>
    </div>
  );
};

export default FormularioGestion;
